/**
 * The Connected-apps drawer's two summaries (Z-4 FX-6; Z-3 cases OB-C-18, OB-A-01): the Microsoft 365 connection line
 * and the "Email processing" summary, and the drawer's other small rules (FU-Z4). Pure: no Angular, no state. ConnectedAppsModalComponent precomputes its fields
 * from these functions, so the template only binds fields.
 *
 * - The connection starts unknown: nothing says "Connected" (and neither Connect nor the gear shows) until
 *   GetMicrosoft365Status answers. A failed check says so instead of guessing, and keeps Connect on offer (running the
 *   consent flow again is the way back; it never disconnects anything).
 * - "Email processing" says nothing until the user's own settings have loaded: before FX-6 an empty settings object
 *   read as "Off" while the real setting was still on its way. A failed load says so.
 * - FU-Z4 Z4-7 #1: the "Forgot ‹sender›" toast escapes the remembered address (the app's toasts render with enableHtml).
 * - FU-Z4 x2: the open drawer is a modal dialog; on close the focus goes back to the element that opened it, when that
 *   element is still in the page.
 */
import { escapeSummaryText } from '@app/main/agenda/contact/lead-bulk.logic';

/** Where the Microsoft 365 status check stands. */
export type M365LinkState = 'unknown' | 'connected' | 'not-connected' | 'failed';

/** Where the load of the user's email settings stands. */
export type ConnectedAppsLoadState = 'loading' | 'loaded' | 'failed';

/** The drawer's summary words. Copy marked † is proposed (not in the design). */
export const CONNECTED_APPS_SUMMARY_COPY = {
    connected: 'Connected',
    notConnected: 'Not connected',
    /** † While GetMicrosoft365Status has not answered yet. */
    checking: 'Checking…',
    /** † GetMicrosoft365Status failed: the state is not known. */
    checkFailed: "Couldn't check the connection",
    emailOn: 'On',
    emailOnFlagged: 'On · Flagged included',
    emailFlaggedOnly: 'Flagged only',
    emailOff: 'Off',
    /** † The user's email settings could not be read. */
    emailLoadFailed: "Couldn't load",
} as const;

/** The Microsoft 365 row, precomputed. */
export interface M365StatusView {
    /** The status check said connected: the green dot and the gear (Reconnect / Disconnect). */
    readonly isConnected: boolean;
    /** The Connect button shows. */
    readonly showConnect: boolean;
    /** The words next to the dot. */
    readonly label: string;
}

/** The answer of GetMicrosoft365Status, as far as the drawer reads it. */
export interface M365StatusAnswer {
    readonly isConnected?: boolean;
}

/** The user's two email toggles, as far as the summary reads them (UserEmailAutomationSettingsDto). */
export interface EmailProcessingFlags {
    readonly emailProcessingEnabled?: boolean;
    readonly processFlaggedEmailsEnabled?: boolean;
}

/** The link state of a GetMicrosoft365Status answer: connected only when the server says so. */
export function m365LinkStateOf(answer: M365StatusAnswer | null | undefined): M365LinkState {
    return answer && answer.isConnected === true ? 'connected' : 'not-connected';
}

/** The Microsoft 365 row of a link state (exhaustive over the four states). */
export function m365StatusView(state: M365LinkState): M365StatusView {
    switch (state) {
        case 'connected':
            return { isConnected: true, showConnect: false, label: CONNECTED_APPS_SUMMARY_COPY.connected };
        case 'not-connected':
            return { isConnected: false, showConnect: true, label: CONNECTED_APPS_SUMMARY_COPY.notConnected };
        case 'failed':
            return { isConnected: false, showConnect: true, label: CONNECTED_APPS_SUMMARY_COPY.checkFailed };
        case 'unknown':
            return { isConnected: false, showConnect: false, label: CONNECTED_APPS_SUMMARY_COPY.checking };
        default:
            return { isConnected: false, showConnect: false, label: CONNECTED_APPS_SUMMARY_COPY.checking };
    }
}

/**
 * The summary next to "Email processing": nothing while the settings load, "Couldn't load" when they failed, else the
 * two toggles. Flag-to-task is independent of the master toggle, so "Flagged only" (master off, flagged on) is a real
 * state.
 */
export function emailProcessingSummary(
    state: ConnectedAppsLoadState,
    settings: EmailProcessingFlags | null | undefined
): string {
    let processing: boolean;
    let flagged: boolean;

    switch (state) {
        case 'loading':
            return '';
        case 'failed':
            return CONNECTED_APPS_SUMMARY_COPY.emailLoadFailed;
        case 'loaded':
            processing = !!settings && !!settings.emailProcessingEnabled;
            flagged = !!settings && !!settings.processFlaggedEmailsEnabled;
            if (processing) {
                return flagged ? CONNECTED_APPS_SUMMARY_COPY.emailOnFlagged : CONNECTED_APPS_SUMMARY_COPY.emailOn;
            }
            return flagged ? CONNECTED_APPS_SUMMARY_COPY.emailFlaggedOnly : CONNECTED_APPS_SUMMARY_COPY.emailOff;
        default:
            return '';
    }
}

/** The drawer's `inert` attribute: present ('') while closed, so nothing inside takes the focus; absent while open. */
export function connectedAppsInertAttr(isOpen: boolean): string | null {
    return isOpen ? null : '';
}

/**
 * The toast after "What Trovve remembers" forgets one sender (FU-Z4 Z4-7 #1): "Forgot ‹address›", the address
 * HTML-escaped, because the app's toasts render with enableHtml (AppComponentBase.toastrOptions) and the address is
 * server data.
 */
export function forgotSenderToast(address: string | null | undefined): string {
    return 'Forgot ' + escapeSummaryText(address);
}

/** What the drawer needs of the element that had the focus when it opened (FU-Z4 x2). */
export interface ConnectedAppsFocusTarget {
    readonly isConnected: boolean;
    focus(options?: FocusOptions): void;
}

/** An element the focus can be given back to: it has `focus()` and says whether it is still in the document. */
export function isConnectedAppsFocusTarget(element: unknown): element is ConnectedAppsFocusTarget {
    const candidate: Partial<ConnectedAppsFocusTarget> | null =
        element !== null && typeof element === 'object' ? (element as Partial<ConnectedAppsFocusTarget>) : null;

    return !!candidate && typeof candidate.focus === 'function' && typeof candidate.isConnected === 'boolean';
}

/**
 * The element to give the focus back to when the drawer closes (FU-Z4 x2): the one that had it when the drawer opened
 * (the opener), when it is still in the document and is not the page body; null = leave the focus where it is.
 */
export function connectedAppsFocusToRestore<T extends { readonly isConnected: boolean }>(
    atOpen: T | null | undefined,
    body: T | null | undefined
): T | null {
    if (!atOpen || atOpen === body || !atOpen.isConnected) {
        return null;
    }
    return atOpen;
}

/** The controls inside the open drawer that Tab moves between (FU-Z4-FIX FZ-3; the drawer filters out hidden ones). */
export const CONNECTED_APPS_FOCUSABLE_SELECTOR =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * FU-Z4-FIX FZ-3: where Tab (or Shift+Tab) must wrap inside the open modal drawer so the focus never leaves it: from the
 * last control to the first (Tab), from the first to the last (Shift+Tab), and from anywhere outside the list (the drawer
 * itself, or a control that went away) to the first or last. Null = let the browser move the focus normally (or, with no
 * control at all, nowhere to go).
 */
export function connectedAppsTabWrapTarget<T>(
    focusables: readonly T[],
    current: T | null | undefined,
    shift: boolean
): T | null {
    const first: T | null = focusables.length > 0 ? focusables[0] : null;
    const last: T | null = focusables.length > 0 ? focusables[focusables.length - 1] : null;
    const inList: boolean = current !== null && current !== undefined && focusables.indexOf(current) >= 0;

    if (!first || !last) {
        return null;
    }
    if (shift) {
        return !inList || current === first ? last : null;
    }
    return !inList || current === last ? first : null;
}
